import React from 'react';
import PropTypes from 'prop-types';
import { Draggable } from 'react-beautiful-dnd';
import styled from '@emotion/styled';
import ReadOnlyExerciseItem from './ReadOnlyExerciseItem';
import OverlapIcon from '../../../../shared/components/OverlapIcon/Overlapicon';
import { BoxShadow } from '../../../../shared/GlobalStyles';

/** Matches DraggableDivWrapper's drag styling (day/week) so the clone
 * looks consistent across views. Pass-through at rest (zero styling) so
 * cells flipping into interactive mode on hover don't shift layout —
 * all visuals turn on only when isDragging is true. The dragged item
 * itself grows slightly on grab, but rbd's placeholder reserves the
 * original size so neighboring items stay put. */
const DragShell = styled('div')`
  position: relative;
  ${(props) => props.isDragging && `
    border-radius: 4px;
    border: 1px dashed #77A7FF;
    box-shadow: ${BoxShadow};
    /* Match ReadOnlyExerciseItem's Container bg: session-break items use
     * #F8F8F8, everything else white. This way the 8px padding-top
     * doesn't render as a white gap between the dashed border and the
     * gray content of a session-break clone. */
    background: ${props.hasSessionBreakBackground ? '#F8F8F8' : 'white'};
    /* For session-break items, ReadOnlyExerciseItem's Container adds its
     * own 8px padding-top (with gray bg) — skip ours so the spacing
     * isn't doubled. Other items don't have that internal padding-top,
     * so we provide 8px ourselves. */
    padding: ${props.hasSessionBreakBackground ? '0 4px 0 4px' : '8px 4px 0 4px'};
    margin-bottom: 4px;
    /* rbd locks the borderBox height inline to match the resting size,
     * which would otherwise squish the content into our padding. Allow
     * the clone to grow so the padding is actually visible. The
     * placeholder still mirrors the resting size, so the list around
     * the drag stays stable. */
    height: auto !important;
  `}
`;

/** Corner badge for session-break drag clones — mirrors
 * RenderCloneDraggableDivWrapper.MultiItemBadge so the visual cue is
 * consistent with day/week view. */
const MultiItemBadge = styled('div')`
  position: absolute;
  right: -10px;
  top: -19px;
  height: 38px;
  width: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #CCCCCC;
  background: #FFFFFF;
  box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
  /* ReadOnlyExerciseItem's Container is position: relative without a
   * z-index, so its background ends up above the badge in document
   * order. Lift the badge into its own stacking context. */
  z-index: 1;
`;

/**
 * Wraps a `ReadOnlyExerciseItem` in a react-beautiful-dnd `Draggable` for
 * use in month view. Mounted only when the parent cell is "interactive"
 * (i.e. the user has hovered or focused into it) — see
 * `MonthDayDroppableCell`. This keeps the initial month render free of
 * Draggable nodes and avoids the perf cost of ~hundreds of mounted
 * Draggables across a dense month grid.
 */
const DraggableMonthExerciseItem = ({
  content,
  index,
  exercises,
  hasSessionBreakBackground,
  isHiddenByDraggedBreak,
}) => {
  /** Count items under this session break (up to the next break). Only
   * relevant when the dragged clone is a session_break — used to render
   * the corner badge that matches day/week view's MultiItemBadge. */
  const sessionBreakItemCount = (() => {
    if (content.type !== 'session_break') return 0;
    let count = 0;
    for (let i = index + 1; i < exercises.length; i += 1) {
      if (exercises[i]?.type === 'session_break') break;
      count += 1;
    }
    return count;
  })();

  return (
    <Draggable
      draggableId={String(content.assignedId)}
      index={index}
    >
      {(provided, snapshot) => {
        /** Hide items that fall under a session break currently being
         * dragged — mirrors week view's shouldCollapseUnderSessionBreak.
         * Keep the Draggable wrapper mounted so rbd's indices stay
         * stable; just zero out the footprint and content. */
        const hideForBreakDrag = isHiddenByDraggedBreak && !snapshot.isDragging;
        const hiddenStyle = hideForBreakDrag
          ? {
            height: 0,
            margin: 0,
            padding: 0,
            overflow: 'hidden',
            opacity: 0,
            pointerEvents: 'none',
          }
          : null;
        const composedStyle = {
          ...(provided.draggableProps.style),
          ...(hiddenStyle || {}),
        };

        return (
          <DragShell
            ref={provided.innerRef}
            isDragging={snapshot.isDragging}
            hasSessionBreakBackground={hasSessionBreakBackground}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={composedStyle}
          >
            {hideForBreakDrag ? (
              <div />
            ) : (
              <>
                {snapshot.isDragging
                  && content.type === 'session_break'
                  && sessionBreakItemCount > 0 && (
                  <MultiItemBadge>
                    <OverlapIcon count={sessionBreakItemCount} />
                  </MultiItemBadge>
                )}
                <ReadOnlyExerciseItem
                  content={content}
                  index={index}
                  exercises={exercises}
                  hasSessionBreakBackground={hasSessionBreakBackground}
                />
              </>
            )}
          </DragShell>
        );
      }}
    </Draggable>
  );
};

DraggableMonthExerciseItem.propTypes = {
  content: PropTypes.shape({
    assignedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
  exercises: PropTypes.arrayOf(PropTypes.shape({})),
  hasSessionBreakBackground: PropTypes.bool,
  isHiddenByDraggedBreak: PropTypes.bool,
};

DraggableMonthExerciseItem.defaultProps = {
  exercises: [],
  hasSessionBreakBackground: false,
  isHiddenByDraggedBreak: false,
};

export default React.memo(DraggableMonthExerciseItem);
